<template>
  <platform-toast-message />
  <div
    class="flex h-full w-full rounded-md"
    :class="{ 'max-h-[98vh] max-w-[98vw] md:max-h-[95vh] md:max-w-[95vw]': config.isWidget }"
  >
    <loading-fullscreen :show="isFullScreenLoading" />
    <platform-main :class="{ 'md:ml-20': config.isWidget }">
      <component :is="currentScreenComponent" />
    </platform-main>
    <platform-controls v-if="config.isWidget" />
  </div>
  <div id="modals" />
</template>

<script setup lang="ts">
import { computed, onMounted, watch, ref, onErrorCaptured } from "vue";
import { useI18n } from "vue-i18n";
import config from "@/config";

import LoadingFullscreen from "@/components/loading/LoadingFullscreen.vue";
import PlatformControls from "@/components/platform/PlatformControls.vue";
import PlatformMain from "@/components/platform/PlatformMain.vue";
import PlatformToastMessage from "@/components/platform/PlatformToastMessage.vue";

import ApplicationWasUpdatedEvent from "@/api/ws/events/ApplicationWasUpdatedEvent";
import useApplicationsState from "@/composables/state/applicationsState";
import useAuthState from "@/composables/state/authState";
import useBrandingState from "@/composables/state/brandingState";
import { useDocumentsState } from "@/composables/state/documentsState";
import useOptionsState from "@/composables/state/optionsState";
import usePlatformScreenState from "@/composables/state/platformScreenState";
import { usePromiseWrapper } from "@/composables/common";
import useToastState from "@/composables/state/toastState";
import { ApplicationPrivateChannel } from "@/api/ws/channels/ApplicationPrivateChannel";
import { BorrowerPrivateChannel } from "@/api/ws/channels/BorrowerPrivateChannel";
import BorrowerConnectedToProfileEvent from "@/api/ws/events/BorrowerConnectedToProfileEvent";
import { BorrowerPlatformTemplatesHttpService } from "@/api/http/BorrowerPlatformTemplatesHttpService";
import { useAnalyticsState } from "./composables/state/analyticsState";
import { AnalyticsEvent } from "./enums/analytics";
import { ApplicationsHttpService } from "./api/http/ApplicationsHttpService";
import useWidgetModalState, { buildResumeWidgetUrl } from "./composables/state/widgetModalState";

const {
  getActiveApplication,
  getApplications,
  getApplicationAndSetAsSelected,
  allApplications,
  selectedApplication,
  setSelectedApplication,
} = useApplicationsState();
const {
  activePlatformTemplate,
  currentScreenComponent,
  showLoadingFullscreen: httpServiceIsLoading,
} = usePlatformScreenState();
const { options, getOptions } = useOptionsState();
const {
  authToken,
  loggedInUser,
  getLoggedInUser,
  loginWithSecret,
  redeemHandoffCode,
  setAuthToken,
} = useAuthState();
const { trackEvent, trackUserPropertiesChange } = useAnalyticsState();
const { isBrandingLoading, getBranding } = useBrandingState();
const { getUploadedDocuments, getRequiredDocuments } = useDocumentsState();
const { open: openEmbeddedWidget } = useWidgetModalState();
const toast = useToastState();
const { t } = useI18n();

const appIsInitalizing = ref(false);
const applicationPrivateChannel = ref<ApplicationPrivateChannel | null>(null);
const borrowerPrivateChannel = ref<BorrowerPrivateChannel | null>(null);

const bodyClassList = config.isWidget ? ["bg-black/60"] : ["bg-gray-100"];
bodyClassList.forEach((className) => {
  if (!document.body.classList.contains(className)) {
    document.body.classList.add(className);
  }
});

const isFullScreenLoading = computed(
  () => appIsInitalizing.value || httpServiceIsLoading.value || isBrandingLoading.value
);

const initApplications = async () => {
  await getApplications();
  const activeApplicationId = selectedApplication.value?.id;
  const firstApplicatonId = activeApplicationId || allApplications.value?.[0]?.id;
  if (firstApplicatonId) {
    await getApplicationAndSetAsSelected(firstApplicatonId);
  }
  await getActiveApplication();

  if (config.embedWidgetOnBoot && selectedApplication.value?.id) {
    const applicationsService = new ApplicationsHttpService();

    const resumeLink = await applicationsService.generateResumeLink(selectedApplication.value?.id, {
      type: "application",
      shorten: false,
    });

    openEmbeddedWidget(buildResumeWidgetUrl(resumeLink));

    // Prevent the user from getting stuck in a loop where the widget
    // embeds after refresh...
    const url = new URL(window.location.href);
    url.searchParams.delete("embedWidgetOnBoot");
    window.history.replaceState(null, "", url);
  }
};

const { executeFetch: getBorrowerPlatformTemplate } = usePromiseWrapper(async () => {
  const bpTemplateApi = new BorrowerPlatformTemplatesHttpService();

  const bpTemplateIdFromQuery = config.platformTemplateId;
  const applicationBpTemplateId = selectedApplication.value?.default_borrower_platform_template_id;
  const fallbackBpTemplateId = options.value?.fallback_borrower_platform_template_id;

  const templateIds = [bpTemplateIdFromQuery, applicationBpTemplateId, fallbackBpTemplateId].filter(
    (id): id is string => Boolean(id) && typeof id === "string"
  );

  for (const templateId of templateIds) {
    try {
      activePlatformTemplate.value = await bpTemplateApi.show(templateId);
      return;
    } catch (error) {
      // Try next template
    }
  }
});

watch(authToken, (newAuthToken, oldToken) => {
  getLoggedInUser();
  if (!newAuthToken || newAuthToken !== oldToken) {
    setSelectedApplication(null);
  }
});

watch(loggedInUser, async (newUserValue, previousUserValue) => {
  if (previousUserValue) {
    borrowerPrivateChannel.value?.leave();
  }
  if (!newUserValue) {
    return;
  }
  borrowerPrivateChannel.value = new BorrowerPrivateChannel(newUserValue.id);
  borrowerPrivateChannel.value.subscribeTo(new BorrowerConnectedToProfileEvent(initApplications));

  await initApplications();
});

watch(
  () => selectedApplication.value?.id,
  (newDealId, previousDealId) => {
    if (previousDealId) {
      applicationPrivateChannel.value?.leave();
    }
    if (newDealId) {
      applicationPrivateChannel.value = new ApplicationPrivateChannel(newDealId);
      applicationPrivateChannel.value.subscribeTo(
        new ApplicationWasUpdatedEvent(async (data) => {
          await Promise.all([
            getApplicationAndSetAsSelected(data.id),
            getUploadedDocuments(data.id),
            getRequiredDocuments(data.id),
          ]);
        })
      );
    }
  },
  { immediate: true }
);

watch(() => selectedApplication.value?.id, getBorrowerPlatformTemplate);

watch(activePlatformTemplate, (template) => {
  if (template) {
    trackUserPropertiesChange({
      borrower_platform_template_id: template.id,
      borrower_platform_template_name: template.name,
    });
  }
});

onErrorCaptured((error, component, info) => {
  toast.value.addMessage({
    title: t("common.errorOccurred"),
    type: "error",
    canClose: false,
    autoClose: false,
    action: {
      label: t("common.refresh"),
      handler: () => window.location.reload(),
    },
  });

  trackEvent(AnalyticsEvent.UnandledErrorThrown, {
    componentName: component?.$options?.__name,
    error: error.message,
    info,
  });
});

onMounted(async () => {
  try {
    appIsInitalizing.value = true;

    await getBranding();

    if (config.handoffCode) {
      await redeemHandoffCode(config.handoffCode);
    }

    if (!config.handoffCode && config.jwt) {
      setAuthToken(config.jwt);
    }

    if (config.email && config.secret && !authToken.value) {
      // https://drive.google.com/file/d/1NMs78wx9t-NlrYSiJppc1JIPOF78WK4p/view?usp=sharing
      await loginWithSecret();
    } else {
      await getLoggedInUser();
    }

    await Promise.all([getOptions(), getBorrowerPlatformTemplate()]);
  } finally {
    appIsInitalizing.value = false;
  }
});
</script>

<style>
@reference "@/tailwind.css";

html,
body,
#app {
  @apply h-full w-full;
}

html {
  @apply text-gray-400;
}

#app {
  @apply flex h-full w-full items-center justify-center;
}

.tippy-box[data-theme~="lendflow"] {
  @apply text-xs leading-5 font-normal break-words text-white;
}

a {
  color: #3769d6;
}
</style>
